import { AnimatePresence, motion } from 'framer-motion';
import { pick } from 'lodash';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { ReactComponent as ArrowIcon } from '../../../../assets/graphics/svg/colorable/cloud_skin02_but_arrow_set4s_00.svg';

import './WEAHeader.css';

import { Images } from '../../../../constants/images';
import { SVGIconType } from '../../../../constants/svgIcon';
import { useSessionStore } from '../../../../stores/SessionStore';
import { useUserStore } from '../../../../stores/UserStore';
import { AvatarNode } from '../../AvatarNode';
import { SvgIcon } from '../../SVGIcon';
import toast from 'react-hot-toast';

export const WEAHeader = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const userId = useSessionStore((state) => state.activeUserId);
  const userAccount = useUserStore((state) => state.getAccount(userId ?? ''));

  const { name, avatar, profilePicture, worksForJoyajoy } = useUserStore(
    (state) => {
      const profile = state.getProfile(userId ?? '');
      return {
        ...pick(profile, [
          'name',
          'avatar',
          'profilePicture',
          'worksForJoyajoy',
        ]),
      };
    },
    shallow,
  );

  const handleClickLogin = useCallback(() => {
    navigate('/wea-member/start');
  }, [navigate]);

  // const handleClickHome = useCallback(() => {
  //   if (location.pathname !== '/') {
  //     navigate('/');
  //   }
  // }, [location.pathname, navigate]);

  const handleOpenDropdown = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const logout = useSessionStore((state) => state.logout);

  const handleLogout = useCallback(async () => {
    toast.loading(t('Please wait while we are signing you out...'));
    setIsOpen(false);
    await logout();
    navigate('/wea-member');
    window.location.reload();
  }, [logout, navigate, t]);

  // const onClickAIMemoryGames = useCallback(() => {
  //   if (
  //     userAccount?.managedClassroomExpiredAt &&
  //     userAccount?.managedClassroomExpiredAt > new Date()
  //   ) {
  //     navigate('/club/journey');
  //   } else {
  //     navigate('/smart-parents');
  //   }
  //   setIsOpen(false);
  // }, [navigate, userAccount?.managedClassroomExpiredAt]);

  const onClickTeacherMembership = useCallback(() => {
    navigate(`/wea-member`);
    setIsOpen(false);
  }, [navigate]);

  const onClickReferrals = useCallback(() => {
    navigate('/wea-member/referral');
    setIsOpen(false);
  }, [navigate]);

  // const onClickClubMemebershipTierTickets = useCallback(() => {
  //   navigate('/club-membership-tier/tickets');
  //   setIsOpen(false);
  // }, [navigate]);

  const onClickAccountSettings = useCallback(() => {
    navigate('/wea-member/account');
  }, [navigate]);

  const notShowUserPartPaths = [
    '/wea-member/start',
    // '/reset-password',
    '/name-card/:id/link',
    '/name-card/:id/collect',
    '/name-card/transfer/:id/link',
    '/name-card/transfer/:id/transferral',
    // '/verify',
    '/club-redeem/:store/rewardOffer/*',
    '/share/game',
    '/share',
    '/tts-enhancement',
    '/wea-member/login',
    '/wea-member/register',
  ];
  const isShowUserParts = !notShowUserPartPaths.some((path) =>
    matchPath(path, location.pathname),
  );

  // clicking outside of the dropdown menu will close it
  useEffect(() => {
    const handleClickOutsideMenu = (event: MouseEvent) => {
      if (menuRef.current) {
        if (
          isOpen === false &&
          menuRef.current.contains(event.target as Node)
        ) {
          setIsOpen(true);
        } else {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutsideMenu);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideMenu);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header
      className="weaHeader"
      // style={{ position: 'sticky', top: '0', zIndex: 100 }}
    >
      <div className="header-container">
        <div className="left">
          <a href="https://worldeducators.org/" target="_self">
            <img
              className="icon"
              src={Images.WorldEducatorsA_Logo_202010_Horizontal_WEA_Eng}
              alt="JOYAJOY Logo"
            />
          </a>
        </div>
        {isShowUserParts && (
          <div>
            {userId ? (
              <div className="right" onClick={handleOpenDropdown}>
                <AvatarNode
                  avatar={{
                    ...avatar,
                    userId,
                    source:
                      profilePicture && profilePicture.publicUrls?.small
                        ? { uri: profilePicture.publicUrls?.small }
                        : '',
                  }}
                  size={40}
                />
                <p className="name">{name}</p>
                <ArrowIcon stroke={'#808080'} className="arrow-icon" />
              </div>
            ) : (
              <p className="login" onClick={handleClickLogin}>
                {t('Login')}
              </p>
            )}
          </div>
        )}
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={menuRef}
            className="drop-down"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {/* <div className="item-container" onClick={onClickAIMemoryGames}>
              <SvgIcon
                name={SVGIconType.BrainX9}
                className={'item-icon'}
                stroke={'#000'}
                fill={'#000'}
              />
              <p className="item-text">{t('AI Memory Games')}</p>
            </div> */}

            {/* <div className="item-container" onClick={onClickPerformanceReport}>
              <SvgIcon
                name={SVGIconType.ScoreAnalysis}
                className={'item-icon'}
                stroke={'#000'}
                fill={'#000'}
              />
              <p className="item-text">{t('Performance Report')}</p>
            </div> */}
            <div className="item-container" onClick={onClickTeacherMembership}>
              <SvgIcon
                name={SVGIconType.BookGame}
                className={'item-icon'}
                stroke={'#000'}
                fill={'#000'}
              />
              <p className="item-text">{t('Teacher Membership')}</p>
            </div>

            <div className="item-container" onClick={onClickReferrals}>
              <SvgIcon
                name={SVGIconType.Referral}
                className={'item-icon'}
                stroke={'#000'}
              />
              <p className="item-text">{t('Referrals')}</p>
            </div>

            <div className="item-container" onClick={onClickAccountSettings}>
              <SvgIcon
                name={SVGIconType.Setting}
                className={'item-icon'}
                stroke={'#000'}
              />
              <p className="item-text">{t('Account Information')}</p>
            </div>

            <div
              className="item-container"
              onClick={() => {
                handleLogout().catch((e) => console.error(e));
              }}
            >
              <SvgIcon
                name={SVGIconType.Logout}
                className={'item-icon'}
                stroke={'#000'}
              />
              <p className="item-text">{t('Logout')}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
});
